import {refreshTokenPath, tokenPath, logoutPath, digestPath, exchangeTokenPath} from 'js/tool/APIProvider';
import client from 'js/tool/graphql/client/apollo';
import {AuthResponse, FbPayload} from 'js/reusable/auth/types';

type getAccessTokenArgs = { 
	username: string; 
	password: string;
	policy: boolean | undefined;
}

export async function refreshAndGetToken() {
	const response = await (await fetch(refreshTokenPath, {method: 'POST', credentials: 'include'})).json();

	if (response && response.access) {
		localStorage.setItem('accessToken', response.access);
		window.dispatchEvent(new Event('storage'));

		return response.access;
	}

	if (localStorage.getItem('accessToken')) {
		localStorage.setItem('invalidationMessage', 'fb:logged out due to inactivity');
	} else {
		localStorage.removeItem('invalidationMessage');
	}

	localStorage.removeItem('accessToken');
	window.dispatchEvent(new Event('storage'));

	return null;
}

export async function getAccessToken({username, password, policy}: getAccessTokenArgs) {
	const response = await (await fetch(tokenPath, {
		method:      'POST',
		credentials: 'include',
		headers:     new Headers({'Content-Type': 'application/json'}),
		body:        JSON.stringify({username, password, policy}),
	})).json();

	return handleAuthResponse(response);
}

export async function getAccessTokenWithFacebook(fbPayload: FbPayload) {
	const response = await (await fetch(tokenPath + '?login_type=fbsso', {
		method:      'POST',
		credentials: 'include',
		headers:     new Headers({'Content-Type': 'application/json'}),
		body:        JSON.stringify(fbPayload),
	})).json();

	return handleAuthResponse(response);
}

function handleAuthResponse(response: AuthResponse): AuthResponse | null {
	if (response && response.access) {
		localStorage.setItem('accessToken', response.access);
		window.dispatchEvent(new Event('storage'));

		return null;
	}

	localStorage.removeItem('accessToken');
	window.dispatchEvent(new Event('storage'));

	return response || {username: 'global:error try again later'};
}

export async function exchangeToken(token: string) {
	const response = await (await fetch(exchangeTokenPath, {
		method:      'POST',
		credentials: 'include',
		headers:     new Headers({'Content-Type': 'application/json'}),
		body:        JSON.stringify({token}),
	})).json();

	handleAuthResponse(response);
}

export async function invalidateRefreshToken() {
	await invalidateRefreshTokenHeader();

	localStorage.removeItem('invalidationMessage');
	window.dispatchEvent(new Event('storage'));
	localStorage.setItem('invalidationMessage', 'fb:logged out in another tab');

	await client.stop();
	await client.clearStore();
}

export async function invalidateRefreshTokenHeader() {
	localStorage.removeItem('accessToken');
	await fetch(logoutPath, {method: 'POST', credentials: 'include'});
}

export function hideInvalidationMessage() {
	localStorage.removeItem('invalidationMessage');
	window.dispatchEvent(new Event('storage'));
}

export async function getPublicDigest(retry = false): Promise<null | string> {
	const response = await fetch(digestPath, {
		method:      'POST',
		credentials: 'include',
		headers:     new Headers({'Authorization': `Bearer ${localStorage.getItem('accessToken')}`}),
	});

	if (response.status === 496 || response.status === 403) {
		if (retry) {
			await invalidateRefreshToken();

			return null;
		}

		await refreshAndGetToken();

		return await getPublicDigest(true);
	} else if (response.status >= 400) {
		return null;
	}

	const jsonResponse = await response.json();

	if (jsonResponse && jsonResponse.token) {
		return jsonResponse.token;
	}

	return null;
}
